import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import type { TrackPoint } from "./offline-draft";
import {
  advanceNativeCursor,
  nativePointToTrackPoint,
  selectUnseenNativePoints,
  type NativeRecorderTrackPoint,
  type NativeRecoveryCursor,
  type NativeTrackingSnapshot,
} from "./native-tracking-recovery";

export type RecorderTrackPoint = TrackPoint & {
  nativePointId?: number;
  activeMilliseconds?: number;
};

type RecorderCallbacks = {
  onPoint: (point: RecorderTrackPoint) => void;
  onError: (message: string) => void;
  onTrackingState?: (state: NativeTrackingSnapshot) => void;
};

type NativePoint = {
  nativePointId?: number;
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  timestamp: number;
  activeMilliseconds?: number;
};

type NativePointPage = {
  points: NativePoint[];
  lastReturnedPointId: number;
  hasMore: boolean;
};

type NativeLocationPermissionState = "prompt" | "prompt-with-rationale" | "granted" | "denied";

type TheraLocationPlugin = {
  checkPermissions(): Promise<{ location: NativeLocationPermissionState }>;
  requestPermissions(): Promise<{ location: NativeLocationPermissionState }>;
  start(options: { activityId: string }): Promise<void>;
  stop(): Promise<void>;
  finalizeTracking(): Promise<NativeTrackingSnapshot>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  discardActivity(options: { activityId: string }): Promise<void>;
  getCurrentPosition(): Promise<NativePoint>;
  getTrackingSnapshot(options?: { activityId?: string }): Promise<NativeTrackingSnapshot>;
  getPointsSince(options: {
    activityId: string;
    afterPointId?: number;
    afterTimestamp?: number;
    limit?: number;
  }): Promise<NativePointPage>;
  addListener(eventName: "point", listener: (point: NativePoint) => void): Promise<PluginListenerHandle>;
  addListener(eventName: "trackingState", listener: (state: NativeTrackingSnapshot) => void): Promise<PluginListenerHandle>;
  addListener(eventName: "error", listener: (error: { message: string }) => void): Promise<PluginListenerHandle>;
};

const nativeLocation = registerPlugin<TheraLocationPlugin>("TheraLocation");
let nativeListeners: PluginListenerHandle[] = [];

let webWatchId: number | null = null;

export function recorderPlatform() {
  return Capacitor.getPlatform() === "android" ? "android" : "web";
}

export function backgroundRecordingAvailable() {
  return Capacitor.getPlatform() === "android";
}

export async function ensureTheraLocationPermission(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") {
    if (!navigator.geolocation) throw new Error("Este dispositivo no ofrece geolocalización.");
    return;
  }

  const current = await nativeLocation.checkPermissions().catch(() => ({ location: "prompt" as NativeLocationPermissionState }));
  if (current.location === "granted") return;
  const requested = await nativeLocation.requestPermissions();
  if (requested.location !== "granted") {
    throw new Error("Permite la ubicación precisa para usar Geo AR. No es necesario iniciar una ruta.");
  }
}

function normalizeNativePoint(point: NativePoint): NativeRecorderTrackPoint | null {
  if (!point.nativePointId || point.nativePointId <= 0) return null;
  return nativePointToTrackPoint({
    nativePointId: point.nativePointId,
    latitude: point.latitude,
    longitude: point.longitude,
    altitude: point.altitude ?? null,
    accuracy: point.accuracy,
    timestamp: point.timestamp,
    activeMilliseconds: point.activeMilliseconds ?? 0,
  });
}

export async function getCurrentTheraPosition(): Promise<TrackPoint> {
  if (Capacitor.getPlatform() === "android") {
    const point = await nativeLocation.getCurrentPosition();

    return {
      latitude: point.latitude,
      longitude: point.longitude,
      altitude: point.altitude ?? null,
      accuracy: point.accuracy,
      timestamp: point.timestamp,
    };
  }

  if (!navigator.geolocation) {
    throw new Error("Este dispositivo no ofrece geolocalización.");
  }

  return new Promise<TrackPoint>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          altitude: position.coords.altitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
      },
      () => reject(new Error("No fue posible obtener la ubicación actual.")),
      {
        enableHighAccuracy: true,
        maximumAge: 2000,
        timeout: 15000,
      },
    );
  });
}
async function installNativeRecorderListeners(callbacks: RecorderCallbacks) {
  await Promise.all(nativeListeners.map((listener) => listener.remove()));
  nativeListeners = await Promise.all([
    nativeLocation.addListener("point", (rawPoint) => {
      const nativePoint = normalizeNativePoint(rawPoint);
      callbacks.onPoint(nativePoint ?? {
        latitude: rawPoint.latitude,
        longitude: rawPoint.longitude,
        altitude: rawPoint.altitude ?? null,
        accuracy: rawPoint.accuracy,
        timestamp: rawPoint.timestamp,
      });
    }),
    nativeLocation.addListener("trackingState", (state) => callbacks.onTrackingState?.(state)),
    nativeLocation.addListener("error", (error) => callbacks.onError(error.message)),
  ]);
}

/** Android-only: vuelve a enlazar listeners JS a una sesion nativa ya activa sin START/RESUME. */
export async function reattachLocationRecorder(callbacks: RecorderCallbacks) {
  if (Capacitor.getPlatform() !== "android") return;
  await installNativeRecorderListeners(callbacks);
}

export async function startLocationRecorder(callbacks: RecorderCallbacks & { activityId?: string }) {
  if (Capacitor.getPlatform() === "android") {
    await installNativeRecorderListeners(callbacks);
    await nativeLocation.start({ activityId: callbacks.activityId ?? crypto.randomUUID() });
    return;
  }
  if (!navigator.geolocation) {
    throw new Error("Este dispositivo no ofrece geolocalización.");
  }
  if (webWatchId !== null) return;
  webWatchId = navigator.geolocation.watchPosition(
    (position) =>
      callbacks.onPoint({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        altitude: position.coords.altitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      }),
    () => callbacks.onError("Activa la ubicación precisa para continuar."),
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
  );
}

/** Android-only recovery API used when THERA returns to the foreground. */
export async function recoverNativeLocationPoints(options: {
  activityId: string;
  cursor?: NativeRecoveryCursor;
  pageSize?: number;
  maxPages?: number;
}): Promise<{ points: NativeRecorderTrackPoint[]; cursor: NativeRecoveryCursor }> {
  if (Capacitor.getPlatform() !== "android") return { points: [], cursor: options.cursor ?? {} };
  const pageSize = Math.min(1000, Math.max(1, options.pageSize ?? 500));
  const maxPages = Math.min(100, Math.max(1, options.maxPages ?? 40));
  let cursor = { ...(options.cursor ?? {}) };
  const recovered: NativeRecorderTrackPoint[] = [];

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const page = await nativeLocation.getPointsSince({
      activityId: options.activityId,
      afterPointId: cursor.afterPointId,
      afterTimestamp: cursor.afterPointId ? undefined : cursor.afterTimestamp,
      limit: pageSize,
    });
    const normalized = page.points
      .map(normalizeNativePoint)
      .filter((point): point is NativeRecorderTrackPoint => Boolean(point));
    const unseen = selectUnseenNativePoints(normalized, cursor);
    recovered.push(...unseen);
    cursor = advanceNativeCursor(cursor, unseen);
    if (!page.hasMore || unseen.length === 0) break;
  }

  return { points: recovered, cursor };
}

export async function getNativeTrackingSnapshot(activityId?: string): Promise<NativeTrackingSnapshot> {
  if (Capacitor.getPlatform() !== "android") return { active: false };
  return nativeLocation.getTrackingSnapshot(activityId ? { activityId } : undefined);
}

export async function discardNativeTrackingActivity(activityId: string): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  await nativeLocation.discardActivity({ activityId });
}

/** Resolves after Android confirms the tracking service is stopped and duration persisted. */
export async function finalizeNativeTracking(): Promise<NativeTrackingSnapshot> {
  if (Capacitor.getPlatform() !== "android") return { active: false };
  return nativeLocation.finalizeTracking();
}

export async function stopLocationRecorder() {
  if (Capacitor.getPlatform() === "android") {
    await nativeLocation.stop();
    await Promise.all(nativeListeners.map((listener) => listener.remove()));
    nativeListeners = [];
    return;
  }
  if (webWatchId === null) return;
  navigator.geolocation.clearWatch(webWatchId);
  webWatchId = null;
}

export async function pauseLocationRecorder() {
  if (Capacitor.getPlatform() === "android") {
    await nativeLocation.pause();
  }
}

export async function resumeLocationRecorder() {
  if (Capacitor.getPlatform() === "android") {
    await nativeLocation.resume();
  }
}
